import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { NetworkSpinnerWrapper } from 'ui/components/NetworkSpinnerWrapper';
import { Auth0WrapperProps } from './type';
import { useDispatch, useSelector } from 'react-redux';
import { addAuth0Interceptors } from '../../../../../helpers/axiosInterceptors';
import { fetchActiveUser, getActiveUser } from '../../../../../services/userV2';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchQuickbooksActiveAccount } from 'services/integrations/quickbooks/redux';
import { fetchShippingIntegrationConnections } from 'services/integrations/shipping/redux';
import { fetchSettingsCompanies } from 'services/settings/company';
import { fetchPaymentTerms } from 'services/paymentTerms';
import { fetchXeroActiveAccount } from 'services/integrations/xero';
import { fetchSellwareActiveAccount } from 'services/commerce/redux';
import { Routes } from 'ui/modules/public/navigation';
import { fetchInsightsEntitlement } from 'services/insights/redux';

const Auth0Handler: React.FC<Auth0WrapperProps> = ({ children }) => {
  const {
    loginWithRedirect,
    isLoading,
    getAccessTokenSilently,
    handleRedirectCallback,
    logout,
  } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [accessTokenLoaded, setAccessTokenLoaded] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const { user, companyExpirationDate } = useSelector(getActiveUser);

  useEffect(() => {
    (async () => {
      try {
        if (params.has('error')) {
          await logout({
            logoutParams: {
              returnTo: process.env.REACT_APP_AUTH0_CALLBACK_URL,
            },
          });
        } else if (params.has('code')) {
          await handleRedirectCallback(window.location.href);
          const accessToken = await getAccessTokenSilently();
          addAuth0Interceptors(accessToken, logout);
          dispatch(fetchActiveUser() as any);
          // @ts-ignore
          dispatch(fetchQuickbooksActiveAccount());
          // @ts-ignore
          dispatch(fetchShippingIntegrationConnections());
          // @ts-ignore
          dispatch(fetchSettingsCompanies());
          // @ts-ignore
          dispatch(fetchPaymentTerms());
          // @ts-ignore
          dispatch(fetchXeroActiveAccount());
          // @ts-ignore
          dispatch(fetchSellwareActiveAccount());
          // @ts-ignore
          dispatch(fetchInsightsEntitlement());
          // set the access token
          setAccessTokenLoaded(true);
          const redirectTo = sessionStorage.getItem('redirectTo');
          if (redirectTo && redirectTo !== '/logout') {
            navigate(redirectTo);
          }
          // temporary redirect for users that get to old registration endpoint
        } else if (
          location.pathname === '/create-account' &&
          process.env.REACT_APP_NEW_REGISTRATION_URL
        ) {
          window.location.href = `${process.env.REACT_APP_NEW_REGISTRATION_URL}?app=drive`;
        } else if (params.has('invitation')) {
          await loginWithRedirect({
            authorizationParams: {
              organization: params.get('organization') ?? '',
              invitation: params.get('invitation') ?? '',
            },
          });
        } else {
          const fullURL = `${location.pathname}${location.search}${location.hash}`;
          sessionStorage.setItem('redirectTo', fullURL);
          loginWithRedirect({
            authorizationParams: {
              redirect_uri: process.env.REACT_APP_AUTH0_CALLBACK_URL,
            },
          }).then();
        }
      } catch (error) {
        console.error('Error in Auth0Handler', error);
        navigate('/logout');
      }
    })();
  }, []);

  useEffect(() => {
    if (
      companyExpirationDate &&
      new Date(companyExpirationDate) < new Date() &&
      location.pathname !== Routes.AccountStatusPage
    ) {
      navigate(Routes.AccountStatusPage);
    }
  }, [companyExpirationDate, location.pathname, history]);

  if (isLoading || !accessTokenLoaded || !user) {
    return (
      <NetworkSpinnerWrapper
        isLoading={isLoading}
        display="flex"
        flexDirection="column"
        overflow="hidden"
        flexGrow={1}
        zIndex={2}
      >
        {/* Empty fragment used as a placeholder for required children props */}
        <></>
      </NetworkSpinnerWrapper>
    );
  }

  return <>{children}</>;
};

export default Auth0Handler;
